import { escapeHtmlAttr } from '@precis-digital/kurama/utils'
import { type Skill } from 'src/copilot/types/skills'
import { type FlowSkillScope } from 'shared/api/copilot'
import { mapAlvieSkillScopeToFlow, parseFlowSkillScope } from 'src/copilot/utils/mapFlowSkill'
import { isSkillSelectable } from 'src/copilot/utils/skills'

/** Parse a composer/chip id to a positive Flow skill integer, or `null` if invalid. */
const parsePositiveIntegerId = (raw: string | number): number | null => {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isInteger(n) || n <= 0) {
    return null
  }
  return n
}

const isPositiveIntegerId = (raw: string | number): boolean => parsePositiveIntegerId(raw) != null

/**
 * Map composer slash-chip ids to Flow `selected_skill_ids` (positive integers).
 * Non-numeric ids are skipped — Flow `selected_skill_ids` must be positive integers.
 */
export const toFlowSelectedSkillIds = (skillIds: ReadonlyArray<string | number>): number[] => {
  const out: number[] = []
  const seen = new Set<number>()
  for (const raw of skillIds) {
    const n = parsePositiveIntegerId(raw)
    if (n == null || seen.has(n)) {
      continue
    }
    seen.add(n)
    out.push(n)
  }
  return out
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Matches a `/{name}` token bounded by whitespace, or by markdown emphasis markers that
 * wrap a chip (`_/Name_`, `**` + `/Name` + `**`; see `htmlToMarkdown`). A closing `_`/`*` only
 * ends the token when followed by whitespace/end, so `/ga4_property` or `/test_v2` do not match `/ga4`.
 * Group 1 is the preserved leading boundary.
 */
const buildSlashTokenPattern = (name: string): RegExp =>
  new RegExp(`(^|[\\s_*])/${escapeRegExp(name)}(?=\\s|$|[_*]+(?:\\s|$))`, 'i')

interface SkillNameFields {
  id: string
  name: string
  /** Flow kebab-case `name` (optional). Used for typed `/slug` matching and `<skill name>`. */
  flowName?: string
}

const skillMatchNames = (skill: SkillNameFields): string[] => {
  const names = [skill.name.trim(), skill.flowName?.trim() ?? '']
  const out: string[] = []
  const seen = new Set<string>()
  for (const name of names) {
    if (name === '' || seen.has(name.toLowerCase())) {
      continue
    }
    seen.add(name.toLowerCase())
    out.push(name)
  }
  return out
}

const longestSkillMatchNameLength = (skill: SkillNameFields): number =>
  Math.max(...skillMatchNames(skill).map((n) => n.length), 0)

/** Longer match names first so shorter prefixes do not win over full titles/slugs. */
const sortByLongestSkillMatchName = <T extends SkillNameFields>(skills: readonly T[]): T[] =>
  [...skills].sort((a, b) => longestSkillMatchNameLength(b) - longestSkillMatchNameLength(a))

/**
 * Remove `/{title|slug}` tokens already represented by composer chips so token
 * matching does not treat chip markdown plus trailing words as a longer skill title.
 */
const stripChippedSlashTokens = (
  message: string,
  chipIds: ReadonlyArray<string | number>,
  skills: readonly SkillNameFields[]
): string => {
  if (message === '' || chipIds.length === 0 || skills.length === 0) {
    return message
  }
  const byId = new Map(skills.map((skill) => [String(skill.id), skill]))
  let remaining = message
  for (const rawId of chipIds) {
    const skill = byId.get(String(rawId))
    if (skill == null) {
      continue
    }
    for (const name of skillMatchNames(skill)) {
      // Same boundary as the pin/rewrite matching, so an italic/bold chip is stripped too.
      remaining = remaining.replace(new RegExp(buildSlashTokenPattern(name), 'gi'), '$1')
    }
  }
  return remaining
}

/**
 * Merge pin ids from slash chips with ids resolved from typed `/Name` tokens.
 * Chip ids win for ordering; token matches fill in skills that were never chipped.
 * Non-numeric chip ids are ignored (Flow pins require positive integers).
 */
export const mergeComposerSkillIds = (
  chipIds: ReadonlyArray<string | number>,
  message: string,
  skills: readonly SkillNameFields[]
): Array<string | number> => {
  const numericChipIds = chipIds.filter(isPositiveIntegerId)
  const messageForTokenMatch = stripChippedSlashTokens(message, numericChipIds, skills)
  const fromTokens = resolveSkillIdsFromSlashTokens(messageForTokenMatch, skills)
  const seen = new Set(numericChipIds.map(String))
  const merged: Array<string | number> = [...numericChipIds]
  for (const id of fromTokens) {
    if (seen.has(String(id)) || !isPositiveIntegerId(id)) {
      continue
    }
    seen.add(String(id))
    merged.push(id)
  }
  return merged
}

/**
 * Drop pinned ids whose skill is missing, paused, or missing a required datasource.
 * Picker rows use the same gate; submit must enforce it too (typed `/Name`, Enter on a
 * disabled slash row, or chips left after datasources were removed).
 */
export const filterSelectableComposerSkillIds = (
  skillIds: ReadonlyArray<string | number>,
  skills: readonly Skill[],
  selectedPlatforms: ReadonlySet<string>
): Array<string | number> => {
  const byId = new Map(skills.map((skill) => [String(skill.id), skill]))
  return skillIds.filter((id) => {
    const skill = byId.get(String(id))
    return skill != null && isSkillSelectable(skill, selectedPlatforms)
  })
}

/**
 * Catalog skills behind composer chips that are no longer selectable (paused or missing
 * a required datasource, e.g. after the user removed it). Submit is blocked for these
 * so a visible chip is never silently sent as plain `/Name` text.
 */
export const getUnavailableChipSkills = (
  chipIds: ReadonlyArray<string | number>,
  skills: readonly Skill[],
  selectedPlatforms: ReadonlySet<string>
): Skill[] => {
  const byId = new Map(skills.map((skill) => [String(skill.id), skill]))
  const seen = new Set<string>()
  const unavailable: Skill[] = []
  for (const id of chipIds) {
    const skill = byId.get(String(id))
    if (skill == null || seen.has(skill.id) || isSkillSelectable(skill, selectedPlatforms)) {
      continue
    }
    seen.add(skill.id)
    unavailable.push(skill)
  }
  return unavailable
}

/**
 * When the user types `/{skill name}` as plain text (no chip), resolve matching
 * catalog skills so we can still pin `selected_skill_ids`. Matches display title
 * and Flow kebab `flowName`. Longer names win first and consume their match so
 * shorter prefixes do not also pin.
 */
export const resolveSkillIdsFromSlashTokens = (message: string, skills: readonly SkillNameFields[]): string[] => {
  if (message === '' || skills.length === 0) {
    return []
  }
  const sorted = sortByLongestSkillMatchName(skills)
  const ids: string[] = []
  const seen = new Set<string>()
  let remaining = message
  for (const skill of sorted) {
    if (seen.has(skill.id)) {
      continue
    }
    for (const name of skillMatchNames(skill)) {
      const pattern = buildSlashTokenPattern(name)
      if (pattern.test(remaining)) {
        seen.add(skill.id)
        ids.push(skill.id)
        remaining = remaining.replace(pattern, '$1')
        break
      }
    }
  }
  return ids
}

/** Flow chat wire format — ADK / precis_agent_v1 expects these tags in the user message. */
export const buildFlowSkillTag = (skill: Skill): string => {
  const trimmed = (skill.flowName ?? skill.name).trim()
  const kebab = trimmed === '' ? skill.name : trimmed
  return (
    `<skill id="${escapeHtmlAttr(skill.id)}" name="${escapeHtmlAttr(kebab)}" ` +
    `title="${escapeHtmlAttr(skill.name)}" scope="${mapAlvieSkillScopeToFlow(skill.scope)}" />`
  )
}

/**
 * Break user-typed `<skill … />` so transcript parsers and Flow do not treat them
 * as pins. Composer chips serialize as `/Title`; only {@link buildFlowSkillTaggedMessage}
 * should inject real wire tags. A zero-width space after `<` keeps the text looking
 * identical while defeating `<skill\b` matchers.
 */
export const neutralizeUserSkillTags = (message: string): string => message.replace(/<skill\b/gi, '<\u200Bskill')

/**
 * Rewrite `/{title|slug}` tokens into Flow `<skill … />` tags for pinned skills.
 * If a pin has no matching slash token (edge case), prepend the tag.
 * Any user-authored `<skill` markup is neutralized first so typed angle brackets
 * (e.g. after `htmlToMarkdown` entity decode) cannot become pins.
 */
export const buildFlowSkillTaggedMessage = (
  message: string,
  skillIds: ReadonlyArray<string | number>,
  skills: readonly Skill[]
): string => {
  const sanitized = neutralizeUserSkillTags(message)
  const pinnedIds = toFlowSelectedSkillIds(skillIds)
  if (pinnedIds.length === 0) {
    return sanitized
  }

  const byId = new Map(skills.map((skill) => [String(skill.id), skill]))
  const pinnedSkills = pinnedIds.map((id) => byId.get(String(id))).filter((skill): skill is Skill => skill != null)

  if (pinnedSkills.length === 0) {
    return sanitized
  }

  let result = sanitized

  for (const skill of sortByLongestSkillMatchName(pinnedSkills)) {
    const tag = buildFlowSkillTag(skill)
    let didReplace = false
    for (const name of skillMatchNames(skill)) {
      // Global: the same skill can be inserted more than once (`/tester /tester`).
      const pattern = new RegExp(buildSlashTokenPattern(name), 'gi')
      const replaced = result.replace(pattern, (_match, prefix: string) => `${prefix}${tag}`)
      if (replaced !== result) {
        result = replaced
        didReplace = true
      }
    }
    if (!didReplace) {
      result = `${tag} ${result}`.trim()
    }
  }

  return result
}

export interface ResolveComposerSkillOutboundOptions {
  /** False for surfaces that hide Skills / `/` (e.g. drawer with `composerActions={[]}`). */
  skillsEnabled: boolean
  selectedPlatforms: ReadonlySet<string>
}

/**
 * Resolve pin ids + Flow wire message for a composer submit.
 * When skills are disabled, skips pin/rewrite (still neutralizes typed `<skill` markup).
 * When enabled, merges chips + `/Name` tokens then drops gated skills before tagging.
 */
export const resolveComposerSkillOutbound = (
  message: string,
  chipIds: ReadonlyArray<string | number>,
  skills: readonly Skill[],
  options: ResolveComposerSkillOutboundOptions
): { outboundMessage: string; skillIds: Array<string | number> } => {
  if (!options.skillsEnabled) {
    return { outboundMessage: neutralizeUserSkillTags(message), skillIds: [] }
  }
  const skillIds = filterSelectableComposerSkillIds(
    mergeComposerSkillIds(chipIds, message, skills),
    skills,
    options.selectedPlatforms
  )
  return {
    outboundMessage: buildFlowSkillTaggedMessage(message, skillIds, skills),
    skillIds
  }
}

const unescapeAttr = (value: string): string =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')

export type SkillMessageSegment =
  | { type: 'text'; value: string }
  | {
      type: 'skill'
      id: string
      title: string
      name: string
      scope: FlowSkillScope
    }

const SKILL_TAG_SEGMENT_REGEX = /<skill\s+([^>]*?)\s*\/>/gi

/**
 * Split a user message into plain text and Flow `<skill … />` segments for
 * rendering scope-coloured pills in the chat transcript.
 */
export const parseSkillMessageSegments = (message: string): SkillMessageSegment[] => {
  if (message === '') {
    return [{ type: 'text', value: '' }]
  }

  const segments: SkillMessageSegment[] = []
  let lastIndex = 0
  SKILL_TAG_SEGMENT_REGEX.lastIndex = 0
  let match = SKILL_TAG_SEGMENT_REGEX.exec(message)
  while (match != null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: message.slice(lastIndex, match.index) })
    }
    const attrs = match[1] ?? ''
    const id = unescapeAttr(/\bid="([^"]*)"/.exec(attrs)?.[1] ?? '')
    const name = unescapeAttr(/\bname="([^"]*)"/.exec(attrs)?.[1] ?? '')
    const title = unescapeAttr(/\btitle="([^"]*)"/.exec(attrs)?.[1] ?? '')
    const scope = parseFlowSkillScope(/\bscope="([^"]*)"/.exec(attrs)?.[1])
    segments.push({
      type: 'skill',
      id: id !== '' ? id : name,
      title: title !== '' ? title : name !== '' ? name : 'skill',
      name: name !== '' ? name : title,
      scope
    })
    lastIndex = match.index + match[0].length
    match = SKILL_TAG_SEGMENT_REGEX.exec(message)
  }
  if (lastIndex < message.length) {
    segments.push({ type: 'text', value: message.slice(lastIndex) })
  }
  return segments.length > 0 ? segments : [{ type: 'text', value: message }]
}

export type SkillPillSegment = Extract<SkillMessageSegment, { type: 'skill' }>

/** Custom element rendered as a skill pill by the transcript `Markdown` `components` override. */
export const SKILL_PILL_TAG = 'skill-pill'

const SKILL_PILL_OPEN_TAG_REGEX = new RegExp(`<(${SKILL_PILL_TAG}\\b)`, 'gi')

/**
 * Rewrite Flow `<skill … />` tags into `<skill-pill data-index="N"></skill-pill>` placeholders
 * so the whole message renders through one full markdown pass (links, `_italic_`, lists).
 * Only the index goes into HTML; pill metadata stays in `pills`. The explicit closing tag is
 * required — HTML parsing ignores `/>` on custom elements and would nest the following text.
 * User-typed `<skill-pill` is neutralized with a zero-width space so it cannot reference a pill.
 */
export const buildSkillPillMarkdown = (message: string): { markdown: string; pills: SkillPillSegment[] } => {
  const pills: SkillPillSegment[] = []
  let markdown = ''
  for (const segment of parseSkillMessageSegments(message)) {
    if (segment.type === 'text') {
      markdown += segment.value.replace(SKILL_PILL_OPEN_TAG_REGEX, '<\u200B$1')
      continue
    }
    markdown += `<${SKILL_PILL_TAG} data-index="${pills.length}"></${SKILL_PILL_TAG}>`
    pills.push(segment)
  }
  return { markdown, pills }
}

/**
 * Per-turn skill pin patch for AG-UI agent state (Flow `AgentState`).
 * Empty pins clear the previous pin so the turn is not stuck with it.
 */
export const buildAgentSkillPinsPatch = (
  skillIds: ReadonlyArray<string | number>
): { selected_skill_ids: number[]; auto_add_skills: false } => ({
  selected_skill_ids: toFlowSelectedSkillIds(skillIds),
  // Flow auto-add stays off until users can toggle it, so only pinned skills reach the chat.
  auto_add_skills: false
})
